import { Controller } from "@hotwired/stimulus"

// difficulty-picker
//
// Wires the "level cards" radio row on the custom-figure form up to a
// live-updating difficulty meter (and a description blurb) so creators
// see exactly what their selection will look like on the figure page
// / trading card back without a page refresh.
//
// The cards themselves are <label>s with click + hover actions and the
// meter element carries `[data-difficulty-meter]` (rendered by the
// difficulty_meter partial). Hovering a card temporarily previews
// that level's meter + description; clicking commits the choice and
// updates the underlying radio button. Mouseleave reverts to the
// committed selection.
export default class extends Controller {
  static targets = ["meter", "description"]

  connect() {
    this.committedDescription = this.hasDescriptionTarget ? this.descriptionTarget.textContent : ""
    this.committedLevel = this.#readMeterLevel()
    this.committedLabel = this.#readMeterLabel()
  }

  // Click handler -- commits the hovered/clicked card as the selected
  // level. The hidden radio button has already been toggled by the
  // browser (label-for-input), so we just sync the meter + caption +
  // description and remember the new committed state.
  select(event) {
    const params = this.#paramsFromEvent(event)
    if (!params) return

    this.committedLevel = params.level
    this.committedLabel = params.label
    this.committedDescription = params.description

    this.#renderMeter(params.level, params.label)
    this.#renderDescription(params.description)
  }

  // Hover-in -- preview that level on the meter + description without
  // touching the underlying radio selection.
  preview(event) {
    const params = this.#paramsFromEvent(event)
    if (!params) return
    this.#renderMeter(params.level, params.label)
    this.#renderDescription(params.description)
  }

  // Hover-out -- snap back to whatever level is actually committed.
  endPreview() {
    this.#renderMeter(this.committedLevel, this.committedLabel)
    this.#renderDescription(this.committedDescription)
  }

  #paramsFromEvent(event) {
    const { level: rawLevel, label, description } = event.params
    const level = parseInt(rawLevel, 10)
    if (isNaN(level)) return null
    return { level, label: label || "", description: description || "" }
  }

  #renderMeter(level, label) {
    this.meterTargets.forEach((meter) => {
      meter.dataset.level = level
      meter.querySelectorAll("[data-bar-idx]").forEach((bar) => {
        const idx = parseInt(bar.dataset.barIdx, 10)
        bar.dataset.lit = (idx <= level).toString()
      })

      const empty = meter.querySelector("[data-meter-empty]")
      const set = meter.querySelector("[data-meter-set]")
      const numEl = meter.querySelector("[data-meter-num]")
      const nameEl = meter.querySelector("[data-meter-name]")

      if (level >= 1 && level <= 5) {
        if (empty) empty.hidden = true
        if (set) set.hidden = false
        if (numEl) numEl.textContent = `Lvl ${level}`
        if (nameEl && label) nameEl.textContent = label
      } else {
        if (empty) empty.hidden = false
        if (set) set.hidden = true
      }
    })
  }

  #renderDescription(text) {
    if (!this.hasDescriptionTarget) return
    this.descriptionTarget.textContent = text
  }

  #readMeterLevel() {
    if (!this.hasMeterTarget) return 0
    const v = parseInt(this.meterTargets[0].dataset.level, 10)
    return isNaN(v) ? 0 : v
  }

  #readMeterLabel() {
    if (!this.hasMeterTarget) return ""
    const nameEl = this.meterTargets[0].querySelector("[data-meter-name]")
    return nameEl ? nameEl.textContent.trim() : ""
  }
}
